import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, CircleNotch, Cube } from "@phosphor-icons/react";

export default function BillingSuccess() {
  const navigate = useNavigate();
  const { refreshSubscription } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | error | timeout
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshSubscription();
          setState("success");
          return;
        }
        if (data.status === "expired") { setState("error"); return; }
      } catch (e) {}
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [refreshSubscription]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="max-w-md w-full border border-border panel p-10 text-center reveal-scale" data-testid="billing-success-card">
        <div className="flex items-center justify-center gap-2 font-heading font-black tracking-tighter mb-8">
          <Cube size={22} weight="bold" /> BuildTrack
        </div>
        {state === "checking" && (
          <>
            <CircleNotch size={44} className="mx-auto animate-spin text-primary mb-4" />
            <h1 className="font-heading text-2xl font-bold">Confirming your payment…</h1>
            <p className="text-muted-foreground text-sm mt-2">This only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle size={48} weight="fill" className="mx-auto text-emerald-600 mb-4" />
            <h1 className="font-heading text-2xl font-bold">You're all set!</h1>
            <p className="text-muted-foreground text-sm mt-2">Your BuildTrack Pro subscription is active. Time to track every dollar.</p>
            <Button onClick={() => navigate("/dashboard")} className="w-full mt-6 rounded-none" data-testid="go-to-dashboard">Go to Dashboard</Button>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <XCircle size={48} weight="fill" className="mx-auto text-destructive mb-4" />
            <h1 className="font-heading text-2xl font-bold">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
            <p className="text-muted-foreground text-sm mt-2">
              {state === "timeout" ? "Your payment is taking longer than expected. It may still complete — check the dashboard shortly." : "We couldn't confirm a completed payment."}
            </p>
            <div className="flex gap-2 mt-6">
              <Button variant="outline" onClick={() => navigate("/dashboard")} className="flex-1 rounded-none" data-testid="success-dashboard">Dashboard</Button>
              <Button onClick={() => navigate("/pricing")} className="flex-1 rounded-none" data-testid="success-retry">Back to Pricing</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
